import React from 'react'
import { Alert, Box, Chip, Grid, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { API_REQUESTS_PAUSED } from '../../data'
import { getIntegrations } from '../../services/admin/integrationsApi'
import { buildExecutionMetrics, getTodayExecutions } from '../../utils/adminIntegrationData'
import { getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'

const buildMetrics = (executions) => {
  const metrics = buildExecutionMetrics(executions)
  return [
    { title: 'Completados', value: String(metrics.completed), icon: <CheckCircleOutlineRoundedIcon /> },
    { title: 'Con advertencias', value: String(metrics.warnings), icon: <WarningAmberRoundedIcon /> },
    { title: 'Con error', value: String(metrics.errors), action: 'Ver errores', icon: <ErrorOutlineRoundedIcon />, path: '/admin/procesos-error' },
    { title: 'Registros tratados', value: new Intl.NumberFormat('es-ES').format(metrics.records), icon: <PublishedWithChangesRoundedIcon /> },
  ]
}

export const AdminProcessesToday = () => {
  const [executions, setExecutions] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined
    let active = true

    const loadExecutions = async () => {
      setLoading(true)
      setErrorMessage('')
      try {
        const response = await getIntegrations({ page: 1, limit: 500, sortDirection: 'asc' })
        if (active) setExecutions(getTodayExecutions(getPaginatedItems(response)))
      } catch (error) {
        if (active) setErrorMessage(getApiErrorMessage(error, 'No se han podido cargar las ejecuciones.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    loadExecutions()
    return () => {
      active = false
    }
  }, [])

  const metrics = buildMetrics(executions)

  return (
    <Box className="page-fade">
      <Box className="page-heading">
        <Typography variant="h4">Procesos hoy</Typography>
        <Typography>Histórico operativo de ejecuciones lanzadas durante la jornada actual.</Typography>
      </Box>

      <Grid container spacing={2.1}>
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Typography className="section-title">Ejecuciones de hoy</Typography>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Proceso</TableCell>
                <TableCell>Integración</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Inicio</TableCell>
                <TableCell>Fin</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Registros</TableCell>
                <TableCell>Duración</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((execution) => (
                <TableRow component={AnimatedTableRow} key={execution.id} hover>
                  <TableCell>{execution.process}</TableCell>
                  <TableCell className="admin-execution-type">{execution.integration}</TableCell>
                  <TableCell>{execution.type}</TableCell>
                  <TableCell>{execution.startLabel}</TableCell>
                  <TableCell>{execution.endLabel}</TableCell>
                  <TableCell>
                    <Chip size="small" className={`admin-status-chip admin-status-${execution.tone}`} label={execution.status} />
                  </TableCell>
                  <TableCell align="right">{execution.records}</TableCell>
                  <TableCell>{execution.duration}</TableCell>
                </TableRow>
              ))}
              {!loading && executions.length === 0 && (
                <TableRow><TableCell colSpan={8}><Typography className="admin-user-empty">No hay ejecuciones registradas hoy.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>
    </Box>
  )
}
